import type { CloudEngine, CloudCarried, CarriedOver } from "@cleo/shared/cloudEngineWords";
import type { CanvasShareData } from "@cleo/shared/canvasShareWords";
import { canvasToolCardModel, type CanvasToolCardModel } from "@cleo/shared/canvasToolCard";
import { deskToolHeadline } from "@cleo/shared/deskToolWords";
import { helperNoticeOf } from "@cleo/shared/helperNoticeWords";

// The notice line's words are shared with Pocket (@cleo/shared/helperNoticeWords).
export { helperNoticeWords } from "@cleo/shared/helperNoticeWords";
import { reportedTimeZone } from "@cleo/shared/worldClock";
import type { SendAnswer, WaitingMessages } from "@cleo/shared/waitingMessageWords";
import { makeFunctionReference } from "convex/server";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useWorldConvexClient } from "./worldConvexClient";

// Can 23 (Jason, 18 Oct 2026: conversations are kept once in the World and shared by every screen): the person's
// conversations kept in the World, the ones Pocket starts among them, as the Desk reads and continues them. The doors are
// Pocket's own (backend/convex/cleoConversationViews.js list, events, live; cleoTurnSend.send; cleoTurns.stop), called
// with the window's own sign-in (worldConvexClient.ts), so the Desk shows exactly what Pocket shows, as the same person.

/** What every screen says while Cleo asks the person's Mac (Can 50; Pocket says the same). */
export const ASKING_YOUR_MAC = "Asking your Mac…";

/** A Pocket conversation's World session (backend cleoConversations.openOrCreate: S_cloud_<key>). */
export const isCloudConversationSession = (sessionId: string): boolean =>
  sessionId.startsWith("S_cloud_");

export type CloudConversationState = "idle" | "running" | "waiting";

export interface CloudConversationSummary {
  readonly conversationId: string;
  readonly roomId: string;
  readonly sessionId: string;
  readonly threadId: string;
  readonly title: string;
  readonly state: CloudConversationState;
  readonly lastSeq: number;
  readonly updatedAt: number;
  /** A copy of an earlier Pocket conversation: closed, it takes no message. */
  readonly movedFrom?: unknown;
  /** The World session of the conversation it was born under (a sub-conversation or a helper). */
  readonly parentSessionId?: string;
  /** A helper Cleo started for that conversation (Can 36: its work is shown as part of the conversation that asked). */
  readonly helper?: boolean;
  /** Can 50 (steps 5.3): the turn waits on a job on the person's Mac, not on the person (backend cleoConversationAccess view). */
  readonly macCall?: { readonly toolName: string; readonly since: number };
  /** Steps 5-7 design §2.4 (step 6.2): the Mac whose chosen folders this conversation uses (born on a Desk with folders), by its name. */
  readonly macTools?: { readonly mac: string; readonly name?: string };
  /** What answers it (backend lib/cleoEngine.js): absent is the World's default tier. */
  readonly engine?: CloudEngine;
}

/**
 * What answers a conversation kept in the World (backend lib/cleoEngine.js): a World tier (`row`), or the person's own plan run by
 * one of their Macs (Can 21, increment 7: `payer: "own-plan"`, the Mac and the engine account it runs on).
 */
export type { CloudEngine } from "@cleo/shared/cloudEngineWords";

/** Whether the conversation answers with the person's own engine on this Mac (`macId`), and on which account. */
export const ownPlanInstanceOn = (
  engine: CloudEngine | undefined,
  macId: string | null,
): string | null =>
  engine && "payer" in engine && engine.payer === "own-plan" && engine.runner.mac === macId
    ? (engine.runner.instance ?? "")
    : null;

const setEngineRef = makeFunctionReference<
  "mutation",
  { conversationId: string; engine: CloudEngine },
  CloudConversationSummary
>("cleoConversations:setEngine");

/** Can 21: the person chooses what answers the conversation next (the World refuses while Cleo answers or waits). */
export function setCloudEngine(
  client: NonNullable<ReturnType<typeof useWorldConvexClient>>,
  conversationId: string,
  engine: CloudEngine,
) {
  return client.mutation(setEngineRef, { conversationId, engine });
}

/** Can 21: what the engine that took over at a switch line was given (backend cleoConversations.carriedOver). */
export type { CloudCarried, CarriedOver } from "@cleo/shared/cloudEngineWords";
const carriedOverRef = makeFunctionReference<
  "query",
  { conversationId: string; seq: number },
  CarriedOver
>("cleoConversations:carriedOver");
/** Can 21, "I can open it": what was carried over at the switch line `seq`, read from the World's own log. */
export function readCarriedOver(
  client: NonNullable<ReturnType<typeof useWorldConvexClient>>,
  conversationId: string,
  seq: number,
) {
  return client.query(carriedOverRef, { conversationId, seq });
}

export interface CloudEvent {
  readonly seq: number;
  readonly kind:
    | "user"
    | "assistant"
    | "tool-call"
    | "tool-result"
    | "gate-wait"
    | "turn-end"
    | "handoff"
    | "helper-notice"
    | "mac-notice"
    | "engine-switch"
    | "canvas-open-request"
    | "canvas-open-observation"
    | "canvas-open-notice";
  readonly body: Readonly<Record<string, unknown>>;
  readonly turnKey?: string | null;
  readonly step?: number | null;
  readonly createdAt: number;
}

export interface CloudLive {
  readonly state: CloudConversationState;
  readonly lastSeq: number;
  readonly turn: { readonly turnKey: string; readonly text: string } | null;
}

const listRef = makeFunctionReference<
  "query",
  { worldKey: string; limit?: number },
  ReadonlyArray<CloudConversationSummary>
>("cleoConversationViews:list");
const eventsRef = makeFunctionReference<
  "query",
  { conversationId: string; limit?: number; before?: number; after?: number },
  { conversation: CloudConversationSummary; events: ReadonlyArray<CloudEvent>; hasMore: boolean }
>("cleoConversationViews:events");
// One-backend plan step 3, increment 3: one of the person's conversations by the World session that holds it, wherever it
// stands in their list (backend/convex/cleoConversationLookup.js).
const bySessionRef = makeFunctionReference<
  "query",
  { worldKey: string; sessionId: string },
  CloudConversationSummary | null
>("cleoConversationLookup:bySession");
const liveRef = makeFunctionReference<"query", { conversationId: string }, CloudLive | null>(
  "cleoConversationViews:live",
);
const sendRef = makeFunctionReference<
  "mutation",
  // timeZone (W-fa19322f): the zone this browser reports for the person, so the World's run knows the day and time where
  // they are; without one the World uses their profile zone.
  // waitIfBusy (W-153d0c44): this screen shows waiting messages, so a message that cannot start a turn now waits its turn
  // (answered { waiting, waitingId, position }) instead of being refused.
  // screen (W-b9529dec): the screen sending it. The Desk's pane (and the web screen, which is the same pane) draws Cleo's choice
  // cards, so the World freezes the turn's instructions with the Desk's words, not Pocket's (backend lib/cleoBoundary.js).
  {
    conversationId: string;
    text: string;
    clientKey: string;
    timeZone?: string;
    waitIfBusy?: boolean;
    screen?: "desk" | "pocket";
  },
  SendAnswer
>("cleoTurnSend:send");
const stopRef = makeFunctionReference<"mutation", { conversationId: string }, { stopped: boolean }>(
  "cleoTurns:stop",
);

// Can 47's approval clause in the browser: when her turn stops at a gate, the pane shows the card and the decision goes
// through the World's own doors (backend/convex/pocketCloudDoors.js, the ones Pocket uses: apps/mobile
// cloudConversationApi.ts), so one approval is used once for that exact action and the turn resumes.

export type GateOutcome = "approved" | "declined";

/** One call's own identity: its turn, step and the World's record of the call. A decision names it. */
export interface CloudOccurrence {
  readonly turnKey: string;
  readonly step: number;
  readonly toolRunId: string;
  readonly toolCallId?: string;
}

/** What the World's record says a waiting conversation waits on right now (pocketCloudDoors.waitingApprovals). */
export interface WaitingApproval {
  readonly gateInstanceKey: string;
  readonly targetDigest: string;
  readonly gateKey: string;
  readonly title: string;
  readonly kind: string;
  readonly decider: string;
  readonly state: "CREATED" | "DECIDED" | "SATISFIED" | string;
  readonly outcome: string | null;
  readonly createdAt: number;
  readonly decidedAt: number | null;
  readonly humanDecides: boolean;
  readonly turnKey: string;
  readonly step: number;
  readonly occurrence?: CloudOccurrence | null;
  readonly waitSeq?: number | null;
  readonly callSeq?: number | null;
  readonly waitingSince?: number | null;
  /** Set when the approval was cancelled because its turn ended before it was used. */
  readonly invalidatedAt?: number | null;
  /** The World's mark: approved only on its card in Cleo on the Desk, so this pane offers Approve only on a card it shows exactly. */
  readonly cardApproved?: boolean;
  readonly cardFacts?: unknown;
  readonly card?: string;
  readonly memberChange?: unknown;
  readonly commercialChange?: unknown;
}

/**
 * The one wait the conversation holds now, as one consistent snapshot: the occurrence, the gate-wait event's seq, the
 * exact call read from the World's record and verified there, and the approval. This, and nothing assembled from the
 * loaded history, decides what the card shows and what a decision names.
 */
export interface CurrentWait {
  readonly state: CloudConversationState;
  readonly occurrence: CloudOccurrence;
  readonly waitSeq: number;
  readonly callSeq: number;
  readonly call: {
    readonly seq: number;
    readonly name: string;
    readonly argumentsJson: string;
    readonly digest: string;
  };
  readonly digestBasis: "call" | "door-derived";
  readonly names?: {
    readonly canvasName: string | null;
    readonly recipient: { readonly name: string; readonly memberCount?: number | null } | null;
    readonly shareData?: CanvasShareData;
  };
  readonly approval: WaitingApproval;
  readonly waitingSince: number | null;
  readonly deadline: number | null;
}

export interface WaitingApprovals {
  readonly state: CloudConversationState;
  readonly approvals: ReadonlyArray<WaitingApproval>;
  readonly current?: CurrentWait | null;
  readonly reason?: string | null;
}

/** The receipt a decision answers with (the World's gate instance, as the Inbox door projects it). */
export interface GateInstanceReceipt {
  readonly gateKey: string;
  readonly gateInstanceKey: string;
  readonly targetDigest: string;
  readonly state: string;
  readonly outcome: string | null;
}

const waitingApprovalsRef = makeFunctionReference<
  "query",
  { conversationId: string },
  WaitingApprovals
>("pocketCloudDoors:waitingApprovals");
// A type, not an interface: Convex's argument constraint wants an index signature an interface lacks.
export type DecideGateInput = {
  readonly conversationId: string;
  readonly gateInstanceKey: string;
  readonly targetDigest: string;
  readonly outcome: GateOutcome;
  readonly decisionKey: string;
  /** The occurrence of the card the person saw: the World refuses a decision that does not name the wait it holds. */
  readonly occurrence: {
    readonly turnKey: string;
    readonly step: number;
    readonly toolRunId: string;
  };
  /** Can 47: the card shown, for an approval approved only on its card. */
  readonly shownCard?: string;
};
const decideGateRef = makeFunctionReference<"mutation", DecideGateInput, GateInstanceReceipt>(
  "pocketCloudDoors:decideGate",
);

/**
 * The World's refusal in its own words (they are written for the person), without the transport's prefix. A refusal
 * is a ConvexError: its sentence travels in `data` (a string, or an object with `message`), while its `message` is
 * what the transport says, which production Convex redacts to "[Request ID: …] Server Error" (W-13189177: the Desk
 * pane showed that for a retired conversation's refusal). So the words come from `data` first, as Pocket reads them
 * (apps/mobile cloudConversationLines.ts cloudErrorText), and from the message only when there is no data.
 */
export function worldWords(error: unknown): string {
  const data = error && typeof error === "object" ? (error as { data?: unknown }).data : undefined;
  const sentence =
    typeof data === "string"
      ? data
      : data &&
          typeof data === "object" &&
          typeof (data as { message?: unknown }).message === "string"
        ? (data as { message: string }).message
        : error instanceof Error
          ? error.message
          : String(error);
  return (
    sentence
      .replace(/^\[CONVEX[^\]]*\]\s*/u, "")
      .replace(/^Uncaught ConvexError:\s*/u, "")
      .replace(/\s*Called by client\s*$/u, "")
      .replace(/\s*Next legal move:.*$/su, "")
      .replace(/^REFUSED:\s*/u, "")
      .replace(/\s*0 writes\.?\s*$/u, "")
      .trim()
      .slice(0, 400) || "The World did not take that."
  );
}

/**
 * The room a new conversation opens in (backend cleoConversations.openOrCreate `room`): a room the World holds, by its id and
 * the World session's label; with `kind: "general"` a general room the World does not hold yet, which it makes with the
 * conversation as the room's root session (one-backend plan step 4: the Desk's + on General).
 */
export interface CloudOpenRoom {
  readonly roomId: string;
  readonly label?: string;
  readonly kind?: "general";
}

/**
 * The conversation a new one opens under (backend cleoConversations.openOrCreate `parent`, #2391): one of the person's own
 * conversations kept in this World, by its World session id; the new one is its sub-session, in its room.
 */
export interface CloudOpenParent {
  readonly sessionId: string;
}

const openOrCreateRef = makeFunctionReference<
  "action",
  {
    worldKey: string;
    clientKey: string;
    title?: string;
    room?: CloudOpenRoom;
    parent?: CloudOpenParent;
    macTools?: { mac: string };
  },
  CloudConversationSummary & { created: boolean }
>("cleoConversations:openOrCreate");

/**
 * A new conversation of the person's in this World through Pocket's own door (backend cleoConversations.openOrCreate), on the
 * World's Standard tier; a resend of the same key is the same conversation. cleo-web opens one this way (webHost.ts). With
 * `room`, it opens in that room of the World's (or, for `kind: "general"`, in a general room the World makes for it); with
 * `parent`, as that conversation's sub-session (the World refuses a room and a parent together).
 */
export async function openCloudConversation(
  client: NonNullable<ReturnType<typeof useWorldConvexClient>>,
  worldKey: string,
  clientKey: string,
  title?: string,
  room?: CloudOpenRoom,
  parent?: CloudOpenParent,
  /** A Desk folder conversation's Mac (steps 5-7 design §2.3): its folder tools use that Mac. */
  macTools?: { readonly mac: string },
) {
  return client.action(openOrCreateRef, {
    worldKey,
    clientKey,
    ...(title ? { title } : {}),
    ...(room ? { room } : {}),
    ...(parent ? { parent } : {}),
    ...(macTools ? { macTools: { mac: macTools.mac } } : {}),
  });
}

/** What the World says while a conversation's Mac has not picked a message up (backend cleoWaitingMessages.macWaiting, §14b.1). */
export interface MacWaiting {
  readonly serverNow: number;
  readonly staleAfterMs: number;
  readonly lastSeenAt?: number;
  readonly macName?: string;
  /** Cleo on that Mac was signed out (its Desk sign-in revoked). */
  readonly signedOut?: boolean;
  readonly awaiting: boolean;
  readonly awaitingSince?: number;
  readonly claimed: boolean;
  /** After a day with no Mac: whether "Answer it on Standard" would succeed now, by the World's own check (backend #2526). */
  readonly again?: boolean;
}
const macWaitingRef = makeFunctionReference<"query", { conversationId: string }, MacWaiting | null>(
  "cleoWaitingMessages:macWaiting",
);
/** Live: whether the conversation's Mac is on and whether its turn waits for that Mac (null for one the World answers). */
export function useMacWaiting(conversationId: string | null) {
  return useWorldQuery<{ conversationId: string }, MacWaiting | null>(
    macWaitingRef,
    conversationId === null ? null : { conversationId },
  );
}
const answerOnStandardRef = makeFunctionReference<
  "mutation",
  { conversationId: string },
  { answered: "now" | "again"; turnKey: string }
>("cleoConversations:answerOnStandard");
/** "Answer now on Neucleos Standard": the person's own press moves the turn no Mac picked up to the World's tier (§14b.1). */
export function answerCloudOnStandard(
  client: NonNullable<ReturnType<typeof useWorldConvexClient>>,
  conversationId: string,
) {
  return client.mutation(answerOnStandardRef, { conversationId });
}

/** How many of the person's newest conversations the Desk reads to name and open them. */
export const CLOUD_LIST_LIMIT = 50;

type Loaded<T> =
  | { readonly status: "loading" }
  | { readonly status: "ready"; readonly value: T }
  | { readonly status: "unavailable"; readonly reason: string };

/** A live read of one World query through the window's sign-in; unavailable when there is none or the World refuses. */
function useWorldQuery<Args extends Record<string, unknown>, T>(
  ref: Parameters<NonNullable<ReturnType<typeof useWorldConvexClient>>["onUpdate"]>[0],
  args: Args | null,
): Loaded<T> {
  const client = useWorldConvexClient();
  const key = args === null ? null : JSON.stringify(args);
  // What the World answered, for the client and arguments it answered for: a change of either reads as loading until
  // the new answer comes, with no state set during the effect itself.
  const [answer, setAnswer] = useState<{
    readonly for: unknown;
    readonly loaded: Loaded<T>;
  } | null>(null);
  useEffect(() => {
    if (key === null || client === null) return;
    const subject = [client, key];
    let stopped = false;
    const unsubscribe = client.onUpdate(
      ref,
      JSON.parse(key) as Args,
      (value) => {
        if (!stopped) setAnswer({ for: subject, loaded: { status: "ready", value: value as T } });
      },
      (error) => {
        if (!stopped)
          setAnswer({
            for: subject,
            loaded: {
              status: "unavailable",
              reason: worldWords(error),
            },
          });
      },
    );
    return () => {
      stopped = true;
      unsubscribe();
    };
  }, [client, key, ref]);
  if (key === null) return { status: "loading" };
  if (client === null)
    return { status: "unavailable", reason: "This window is not signed in to the World." };
  const subject = answer?.for as [unknown, string] | undefined;
  return subject && subject[0] === client && subject[1] === key
    ? answer!.loaded
    : { status: "loading" };
}

/** The person's newest conversations kept in this World, by their World session. */
export function useCloudConversations(
  worldKey: string | null,
): ReadonlyMap<string, CloudConversationSummary> {
  const loaded = useWorldQuery<
    { worldKey: string; limit: number },
    ReadonlyArray<CloudConversationSummary>
  >(listRef as never, worldKey === null ? null : { worldKey, limit: CLOUD_LIST_LIMIT });
  return useMemo(
    () =>
      new Map(
        loaded.status === "ready" ? loaded.value.map((row) => [row.sessionId, row] as const) : [],
      ),
    [loaded],
  );
}

/** One of the person's conversations kept in this World, by its World session: any of them, not only the newest page. */
export function useCloudConversationBySession(
  worldKey: string | null,
  sessionId: string | null,
): Loaded<CloudConversationSummary | null> {
  return useWorldQuery<{ worldKey: string; sessionId: string }, CloudConversationSummary | null>(
    bySessionRef as never,
    worldKey === null || sessionId === null ? null : { worldKey, sessionId },
  );
}

/** One conversation's newest events (a page of `limit`) and the reply she is writing now, live. */
export function useCloudConversation(conversationId: string | null, limit = 200) {
  const events = useWorldQuery<
    { conversationId: string; limit: number },
    { events: ReadonlyArray<CloudEvent>; hasMore: boolean }
  >(eventsRef as never, conversationId === null ? null : { conversationId, limit });
  const live = useWorldQuery<{ conversationId: string }, CloudLive | null>(
    liveRef as never,
    conversationId === null ? null : { conversationId },
  );
  return { events, live };
}

/** The pane's page of events: the newest page it follows live, and each earlier page "Show earlier" brings. */
export const CLOUD_EVENTS_PAGE = 100;

/** Every event the pane holds, once, in seq order: `held` with `page` merged in (a page's copy of an event wins). Pure. */
export function mergeCloudEvents(
  held: ReadonlyArray<CloudEvent>,
  page: ReadonlyArray<CloudEvent>,
): ReadonlyArray<CloudEvent> {
  const bySeq = new Map<number, CloudEvent>(held.map((event) => [event.seq, event]));
  for (const event of page) bySeq.set(event.seq, event);
  return [...bySeq.values()].sort((a, b) => a.seq - b.seq);
}

/**
 * The seq after which events are missing between what the pane holds and a newer page that slid past it (more events
 * arrived between two answers than one page holds), or null when the page joins what is held. Pure.
 */
export function cloudEventGap(
  held: ReadonlyArray<CloudEvent>,
  page: ReadonlyArray<CloudEvent>,
): number | null {
  const heldLast = held.at(-1)?.seq;
  const pageFirst = page[0]?.seq;
  return heldLast !== undefined && pageFirst !== undefined && pageFirst > heldLast + 1
    ? heldLast
    : null;
}

/**
 * W-a2058a59: the first hole in the events held (each held in seq order): the seq after which events are missing, or null when
 * they run on without one. A newer page that slid past what was held leaves one; so does a catch-up read cut short.
 */
export function cloudEventHole(held: ReadonlyArray<CloudEvent>): number | null {
  for (let i = 1; i < held.length; i++)
    if (held[i]!.seq > held[i - 1]!.seq + 1) return held[i - 1]!.seq;
  return null;
}

/** How many forward pages one catch-up reads before it hands back (it goes on at the next change). */
export const CLOUD_HOLE_PAGES = 50;
/** How many times a catch-up read that fails is tried, and how long it waits before each retry. */
export const CLOUD_HOLE_TRIES = 3;
export const CLOUD_HOLE_RETRY_MS = 2_000;

/**
 * Fills every hole in `held`, page by page, forward from where each starts, until the World says nothing more is there
 * (`hasMore` false or an empty page) or CLOUD_HOLE_PAGES were read. `read(after)` reads one page. A read that throws is
 * tried CLOUD_HOLE_TRIES times in all; then the error is returned with what was filled so far. Pure but for `read`/`wait`.
 */
export async function fillCloudHoles(
  held: ReadonlyArray<CloudEvent>,
  read: (
    after: number,
  ) => Promise<{ readonly events: ReadonlyArray<CloudEvent>; readonly hasMore: boolean }>,
  wait: (ms: number) => Promise<void> = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
): Promise<{ readonly events: ReadonlyArray<CloudEvent>; readonly error: unknown }> {
  let events = held;
  for (let pages = 0; pages < CLOUD_HOLE_PAGES; pages++) {
    const after = cloudEventHole(events);
    if (after === null) return { events, error: null };
    let page: { readonly events: ReadonlyArray<CloudEvent>; readonly hasMore: boolean } | null =
      null;
    for (let tried = 1; page === null; tried++) {
      try {
        page = await read(after);
      } catch (error) {
        if (tried >= CLOUD_HOLE_TRIES) return { events, error };
        await wait(CLOUD_HOLE_RETRY_MS * tried);
      }
    }
    const next = mergeCloudEvents(events, page.events);
    // Nothing new came back for this hole: the World holds nothing there now (never read the same page forever).
    if (next.length === events.length) return { events: next, error: null };
    events = next;
  }
  return { events, error: null };
}

/**
 * One conversation's whole history as the pane shows it (one-backend plan step 3, increment 3): its newest page, live,
 * and every earlier page the person asks for, back to its first event, each event once. `hasEarlier` while an earlier
 * event remains unread; `loadEarlier` reads the page just before the first one held (the World's `before` page).
 */
export function useCloudConversationHistory(conversationId: string | null) {
  const client = useWorldConvexClient();
  const { events: newest, live } = useCloudConversation(conversationId, CLOUD_EVENTS_PAGE);
  const [held, setHeld] = useState<{
    readonly for: string | null;
    readonly events: ReadonlyArray<CloudEvent>;
    /** Whether the first event of the conversation is held; null until the first page has answered. */
    readonly first: boolean | null;
  }>({ for: null, events: [], first: null });
  const heldRef = useRef(held);
  heldRef.current = held;
  const [earlier, setEarlier] = useState<{
    readonly for: string | null;
    readonly loading: boolean;
    readonly refusal: string | null;
  }>({
    for: null,
    loading: false,
    refusal: null,
  });

  useEffect(() => {
    if (newest.status !== "ready" || conversationId === null) return;
    const page = newest.value.events;
    setHeld((prev) => {
      const base =
        prev.for === conversationId ? prev : { for: conversationId, events: [], first: null };
      return {
        ...base,
        events: mergeCloudEvents(base.events, page),
        first: base.first ?? !newest.value.hasMore,
      };
    });
  }, [newest, conversationId]);

  // W-a2058a59: every hole in what is held (the events that slid past between two answers, however many) is read forward,
  // page by page, until it is closed; a read that keeps failing is said (holeRefusal), and Try again (retryHoles) reads again.
  const [hole, setHole] = useState<{
    readonly for: string | null;
    readonly refusal: string | null;
    readonly attempt: number;
  }>({
    for: null,
    refusal: null,
    attempt: 0,
  });
  const holeAt = held.for === conversationId ? cloudEventHole(held.events) : null;
  const attempt = hole.for === conversationId ? hole.attempt : 0;
  const refused = hole.for === conversationId && hole.refusal !== null;
  useEffect(() => {
    // Each run fills on its own; one whose conversation, hole or attempt changed meanwhile is dropped (the next run reads again),
    // so a fill cut short by a switch away and back is never left without another (Codex review of #2440).
    if (holeAt === null || client === null || conversationId === null || refused) return;
    let stopped = false;
    void fillCloudHoles(heldRef.current.events, (after) =>
      client.query(eventsRef, { conversationId, after, limit: 200 }),
    ).then(({ events, error }) => {
      if (stopped) return;
      setHeld((prev) =>
        prev.for === conversationId
          ? { ...prev, events: mergeCloudEvents(prev.events, events) }
          : prev,
      );
      setHole({ for: conversationId, refusal: error === null ? null : worldWords(error), attempt });
    });
    return () => {
      stopped = true;
    };
  }, [holeAt, client, conversationId, refused, attempt]);
  const retryHoles = useCallback(() => {
    setHole((prev) => ({
      for: conversationId,
      refusal: null,
      attempt: (prev.for === conversationId ? prev.attempt : 0) + 1,
    }));
  }, [conversationId]);

  const loading = earlier.for === conversationId && earlier.loading;
  const loadEarlier = useCallback(async () => {
    const now = heldRef.current;
    const firstHeld = now.events[0];
    if (
      client === null ||
      conversationId === null ||
      now.for !== conversationId ||
      now.first !== false ||
      !firstHeld
    )
      return;
    setEarlier({ for: conversationId, loading: true, refusal: null });
    try {
      const page = await client.query(eventsRef, {
        conversationId,
        before: firstHeld.seq,
        limit: CLOUD_EVENTS_PAGE,
      });
      setHeld((prev) =>
        prev.for === conversationId
          ? { ...prev, events: mergeCloudEvents(prev.events, page.events), first: !page.hasMore }
          : prev,
      );
      setEarlier({ for: conversationId, loading: false, refusal: null });
    } catch (error) {
      setEarlier({ for: conversationId, loading: false, refusal: worldWords(error) });
    }
  }, [client, conversationId]);

  const ready = newest.status === "ready" && held.for === conversationId;
  return {
    status:
      newest.status === "unavailable"
        ? ("unavailable" as const)
        : ready
          ? ("ready" as const)
          : ("loading" as const),
    reason: newest.status === "unavailable" ? newest.reason : null,
    events: ready ? held.events : [],
    hasEarlier: ready && held.first === false,
    loadingEarlier: loading,
    earlierRefusal: earlier.for === conversationId ? earlier.refusal : null,
    loadEarlier,
    /** Part of the history could not be read after retries (W-a2058a59); retryHoles reads it again. */
    holeRefusal: ready && hole.for === conversationId ? hole.refusal : null,
    retryHoles,
    live,
  };
}

/**
 * The person's message into the conversation, once (a resend of the same key is the same message), with the zone this
 * browser reports for them (W-fa19322f: the World's run then knows the day and time where they are). While Cleo answers
 * or waits on a card it waits its turn (W-153d0c44: this screen shows the waiting messages).
 */
// Step 5.5: the wake offer's Finish press (backend cleoMacMisses.finish): the person's message, once; refused when the line no longer holds.
const finishMacMissRef = makeFunctionReference<
  "mutation",
  { conversationId: string; missId: string; timeZone?: string },
  { seq: number; turnKey: string; created: boolean }
>("cleoMacMisses:finish");
export async function finishMacMiss(
  client: NonNullable<ReturnType<typeof useWorldConvexClient>>,
  conversationId: string,
  missId: string,
): Promise<{ seq: number; turnKey: string; created: boolean }> {
  const timeZone = reportedTimeZone();
  return client.mutation(finishMacMissRef, {
    conversationId,
    missId,
    ...(timeZone ? { timeZone } : {}),
  });
}

export async function sendCloudMessage(
  client: NonNullable<ReturnType<typeof useWorldConvexClient>>,
  conversationId: string,
  text: string,
  clientKey: string,
): Promise<SendAnswer> {
  const timeZone = reportedTimeZone();
  return client.mutation(sendRef, {
    conversationId,
    text,
    clientKey,
    ...(timeZone ? { timeZone } : {}),
    waitIfBusy: true,
    screen: "desk",
  });
}

// W-153d0c44: the person's waiting messages in the conversation (backend cleoWaitingMessages.js): what waits, in their order,
// and the doors that move, edit, discard, or send the paused ones on. Each refuses a message no longer waiting, writing nothing.
type WaitingDoor<Args> = Args & { conversationId: string };
const waitingRef = makeFunctionReference<"query", { conversationId: string }, WaitingMessages>(
  "cleoWaitingMessages:waiting",
);
const moveWaitingRef = makeFunctionReference<
  "mutation",
  WaitingDoor<{ waitingId: string; direction: "up" | "down" }>,
  { moved: boolean }
>("cleoWaitingMessages:moveWaiting");
const editWaitingRef = makeFunctionReference<
  "mutation",
  WaitingDoor<{ waitingId: string; text: string }>,
  { edited: boolean }
>("cleoWaitingMessages:editWaiting");
const discardWaitingRef = makeFunctionReference<
  "mutation",
  WaitingDoor<{ waitingId: string }>,
  { discarded: boolean }
>("cleoWaitingMessages:discardWaiting");
const sendWaitingNowRef = makeFunctionReference<
  "mutation",
  WaitingDoor<{ generation: number }>,
  { resumed: boolean }
>("cleoWaitingMessages:sendWaitingNow");
/** Live: the conversation's waiting messages and their pause (null asks nothing). */
export function useCloudWaitingMessages(conversationId: string | null): Loaded<WaitingMessages> {
  return useWorldQuery<{ conversationId: string }, WaitingMessages>(
    waitingRef as never,
    conversationId === null ? null : { conversationId },
  );
}
type WorldClient = NonNullable<ReturnType<typeof useWorldConvexClient>>;
export const moveCloudWaiting = (
  client: WorldClient,
  conversationId: string,
  waitingId: string,
  direction: "up" | "down",
) => client.mutation(moveWaitingRef, { conversationId, waitingId, direction });
export const editCloudWaiting = (
  client: WorldClient,
  conversationId: string,
  waitingId: string,
  text: string,
) => client.mutation(editWaitingRef, { conversationId, waitingId, text });
export const discardCloudWaiting = (
  client: WorldClient,
  conversationId: string,
  waitingId: string,
) => client.mutation(discardWaitingRef, { conversationId, waitingId });
/** "Send them now": names the pause the person saw; another pause (a later Stop) is not lifted by it. */
export const sendCloudWaitingNow = (
  client: WorldClient,
  conversationId: string,
  generation: number,
) => client.mutation(sendWaitingNowRef, { conversationId, generation });

// W-73d0fe1e increment 4: the files a person attached to a message (backend cleoAttachments.js). A message names them by their
// library object ids (cleoTurnSend.send: body.attachments; a waiting message's `attachments`); what each is, and a short-lived
// address to read it, come from the World's owner-only doors, so only the person who gave a file sees it.

/** One of the person's files, as the World describes it (cleoAttachments:attachment). */
export interface CloudAttachment {
  readonly objectId: string;
  readonly sha256: string;
  readonly name: string;
  readonly mediaType: string;
  readonly byteLength: number;
  readonly createdAt: number;
}
const attachmentRef = makeFunctionReference<"query", { objectId: string }, CloudAttachment>(
  "cleoAttachments:attachment",
);
const attachmentReadUrlRef = makeFunctionReference<
  "action",
  { objectId: string },
  { url: string; expiresAt: number; mediaType: string }
>("cleoAttachments:readUrl");

/** The file ids a message names, in its order, each once; anything that is not an id is left out. Pure. */
export function messageAttachmentIds(value: unknown): ReadonlyArray<string> {
  if (!Array.isArray(value)) return [];
  const ids: string[] = [];
  for (const id of value) if (typeof id === "string" && id && !ids.includes(id)) ids.push(id);
  return ids;
}

/** Live: what one of the person's files is (null asks nothing). */
export function useCloudAttachment(objectId: string | null): Loaded<CloudAttachment> {
  return useWorldQuery<{ objectId: string }, CloudAttachment>(
    attachmentRef as never,
    objectId === null ? null : { objectId },
  );
}

/** A short-lived (two minutes) address to read one of the person's own files; the World refuses anyone else. */
export function readCloudAttachmentUrl(client: WorldClient, objectId: string) {
  return client.action(attachmentReadUrlRef, { objectId });
}

/** Whether a file shows as its picture (the image types a person may attach). */
export const isPictureAttachment = (mediaType: string): boolean =>
  /^image\/(png|jpeg|gif|webp)$/u.test(mediaType.split(";")[0]!.trim().toLowerCase());

/** A file's size in plain words: "900 bytes", "1.2 KB", "2.5 MB". Pure. */
export function attachmentSizeWords(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  const [size, unit] = bytes < 1024 * 1024 ? [bytes / 1024, "KB"] : [bytes / (1024 * 1024), "MB"];
  return `${(Math.round(size * 10) / 10).toString()} ${unit}`;
}

/** The person's Stop of the conversation's running or waiting work. */
export async function stopCloudConversation(
  client: NonNullable<ReturnType<typeof useWorldConvexClient>>,
  conversationId: string,
) {
  return client.mutation(stopRef, { conversationId });
}

/**
 * What the conversation waits on right now, from the World's own record, live; asked only while the conversation waits
 * or a wait is on screen (null asks nothing).
 */
export function useWaitingApprovals(conversationId: string | null): Loaded<WaitingApprovals> {
  return useWorldQuery<{ conversationId: string }, WaitingApprovals>(
    waitingApprovalsRef as never,
    conversationId === null ? null : { conversationId },
  );
}

/** The person's own Approve or Decline of the one approval the conversation waits on, through the World's door. */
export async function decideCloudGate(
  client: NonNullable<ReturnType<typeof useWorldConvexClient>>,
  input: DecideGateInput,
) {
  return client.mutation(decideGateRef, input);
}

/** What a waiting call is, for the card: the recorded call joined to its wait (Pocket's CloudGateWait). */
export interface CloudGateWait {
  readonly seq: number;
  readonly toolCallId: string;
  readonly name: string;
  /** The digest of the exact call: the gate instance's targetDigest. */
  readonly digest: string;
  readonly text: string;
  readonly turnKey: string | null;
  readonly step: number | null;
  /** The recorded arguments of the exact call Cleo wants to make; null until that tool-call event is loaded. */
  readonly argumentsJson: string | null;
}

/** One row of the conversation as the Desk shows it. */
export type CloudRow =
  | { readonly kind: "canvas-notice"; readonly key: string; readonly text: string }
  | {
      readonly kind: "user";
      readonly key: string;
      readonly text: string;
      readonly at: number;
      /** W-73d0fe1e: the person's files on the message, by their library object ids (empty: none). */
      readonly attachments: ReadonlyArray<string>;
      /** Can 21: the engine never started, so this message was not sent (its turn's ending says `unsent`). */
      readonly unsent?: boolean;
    }
  | {
      readonly kind: "assistant";
      readonly key: string;
      readonly text: string;
      readonly streaming: boolean;
    }
  | {
      readonly kind: "step";
      readonly key: string;
      readonly label: string;
      readonly outcome: "running" | "done" | "error" | "not-run";
      /** Can 14: the Canvas this call made or saved (a done create or save), shown in the conversation as its page. */
      readonly canvas?: CanvasToolCardModel;
    }
  | {
      readonly kind: "waiting";
      readonly key: string;
      readonly label: string;
      readonly wait: CloudGateWait;
    }
  | {
      readonly kind: "ended";
      readonly key: string;
      readonly outcome: "stopped" | "failed";
      readonly message: string | null;
      readonly partial: string | null;
    }
  | { readonly kind: "handoff"; readonly key: string }
  // Can 21: an engine the person picked has started; the line says what it was given, and Open reads it (readCarriedOver).
  | {
      readonly kind: "switch";
      readonly key: string;
      readonly seq: number;
      readonly from: CloudEngine | undefined;
      readonly to: CloudEngine;
      readonly carried: CloudCarried;
    }
  // Can 21: the engine could not start, so the person's message above was not sent; the World's words, and those of the message.
  | {
      readonly kind: "unsent";
      readonly key: string;
      readonly turnKey: string;
      readonly message: string;
      readonly text: string;
    }
  // One-backend plan step 3, 4b-ii-b: a World helper this conversation started waits on the person, or died. The World's own
  // words (the helper's name, a catalog title): a line for the person, never Cleo's turn and never the person's message.
  | {
      readonly kind: "notice";
      readonly key: string;
      readonly noticeKind: "waiting" | "died";
      readonly title: string;
      readonly what: string;
      readonly helperSessionId: string;
    }
  // Step 5.5 (Can 50: "when it wakes she offers to finish"): the World's line once the person's Mac, missed as asleep or not
  // answering, is back. Its Finish press sends the person's message once (backend cleoMacMisses.finish).
  | {
      readonly kind: "mac-back";
      readonly key: string;
      readonly text: string;
      readonly missId: string;
    };

const text = (value: unknown): string => (typeof value === "string" ? value : "");
const count = (value: unknown): number | null =>
  typeof value === "number" && Number.isInteger(value) && value >= 0 ? value : null;
/** A switch line's counts, or null when the line does not carry them whole. */
function carriedOf(value: unknown): CloudCarried | null {
  if (!value || typeof value !== "object") return null;
  const { messages, calls, omitted } = value as Record<string, unknown>;
  const m = count(messages),
    c = count(calls),
    o = count(omitted);
  return m === null || c === null || o === null ? null : { messages: m, calls: c, omitted: o };
}

/** What a World tool call did, in the person's words. */
export function cloudStepLabel(name: string, argumentsJson: unknown): string {
  let args: Record<string, unknown> = {};
  try {
    const parsed: unknown = JSON.parse(text(argumentsJson) || "{}");
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed))
      args = parsed as Record<string, unknown>;
  } catch {
    // A call's arguments that do not parse still name its tool.
  }
  if (name === "neucleos_canvas") {
    const action = text(args.action);
    const canvas: Record<string, string> = {
      search: "Search your Canvases",
      catalog: "List your Canvases",
      read: "Read a Canvas",
      save: "Save a Canvas",
      "canvas-share": "Share a Canvas",
      "canvas-sharing": "Read who a Canvas is shared with",
      "sharing-audience": "Read who a Canvas can be shared with",
      describe: "Read how to do it",
      guide: "Read how to do it",
    };
    return canvas[action] ?? "Work on a Canvas";
  }
  if (name === "neucleos_search_tool_catalog") return "Look for a tool";
  if (name === "neucleos_read_world_rooms") return "Read your rooms and sessions";
  return deskToolHeadline(name, args) ?? name.replace(/^neucleos_/u, "").replace(/_/gu, " ");
}

/**
 * Each call's own result, by the call's seq: the first result after it in the same turn, for the same call id and the
 * same tool (Codex review of #2525: a call id can come again, so a result found by its id alone could be another call's).
 */
function resultsOfCalls(
  events: ReadonlyArray<CloudEvent>,
): ReadonlyMap<number, Readonly<Record<string, unknown>>> {
  const open = new Map<string, { readonly seq: number; readonly name: string }>();
  const paired = new Map<number, Readonly<Record<string, unknown>>>();
  for (const event of events) {
    const id = `${event.turnKey ?? ""}|${text(event.body.toolCallId)}`;
    if (event.kind === "tool-call") open.set(id, { seq: event.seq, name: text(event.body.name) });
    else if (event.kind === "tool-result") {
      const call = open.get(id);
      // A result that names no tool (written before results carried it) is its call's; one that names another tool is not.
      if (call && (!text(event.body.name) || call.name === text(event.body.name)))
        paired.set(call.seq, event.body);
      open.delete(id);
    }
  }
  return paired;
}

/**
 * The Canvas a call made or saved, from that call's own successful result, folded as Pocket folds it (the shared
 * canvasToolCardModel); null for any other call, a call whose arguments do not read as a create or save, or no result.
 */
function canvasOfCall(
  name: string,
  argumentsJson: unknown,
  result: Readonly<Record<string, unknown>> | undefined,
): CanvasToolCardModel | null {
  if (!result || result.isError === true || result.ended === true) return null;
  let input: unknown;
  try {
    input = JSON.parse(text(argumentsJson));
  } catch {
    return null;
  }
  const action =
    input && typeof input === "object" ? (input as Record<string, unknown>).action : undefined;
  if (action !== "create" && action !== "save") return null;
  return canvasToolCardModel(name, {
    toolName: name,
    input,
    output: { isError: false, text: text(result.text) },
  });
}

/**
 * The conversation's rows, in order: the person's messages, her replies (and the reply she is writing now), each call
 * she made and how it ended, a wait on the person's decision while it is open, and how a turn that did not complete
 * ended. Pure.
 */
export function cloudConversationRows(
  events: ReadonlyArray<CloudEvent>,
  live: CloudLive | null,
): ReadonlyArray<CloudRow> {
  const calls = new Map<string, unknown>();
  const endedTurns = new Set<string>();
  // The calls answered, by turn and call id: a wait whose call was answered (approved and run, declined, or ended) is
  // over, so its card goes and the call's step row says how it went, as Pocket's feed does (openGateWaits).
  const answered = new Set<string>();
  for (const event of events) {
    if (event.kind === "tool-result") {
      answered.add(`${event.turnKey ?? ""}|${text(event.body.toolCallId)}`);
    }
    if (event.kind === "tool-call")
      calls.set(text(event.body.toolCallId), event.body.argumentsJson);
    if (event.kind === "turn-end" && event.turnKey) endedTurns.add(event.turnKey);
  }
  // Can 21: the turns whose message was never sent (the engine did not start), and that message's words.
  const unsentTurns = new Set(
    events
      .filter((event) => event.kind === "turn-end" && event.body.unsent === true && event.turnKey)
      .map((event) => event.turnKey as string),
  );
  const unsentWords = new Map<string, string>();
  const ownResults = resultsOfCalls(events);
  const rows: CloudRow[] = [];
  for (const event of events) {
    const key = `seq-${event.seq}`;
    switch (event.kind) {
      case "user": {
        const unsent = event.turnKey ? unsentTurns.has(event.turnKey) : false;
        if (unsent) unsentWords.set(event.turnKey as string, text(event.body.text));
        rows.push({
          kind: "user",
          key,
          text: text(event.body.text),
          at: event.createdAt,
          attachments: messageAttachmentIds(event.body.attachments),
          ...(unsent ? { unsent: true } : {}),
        });
        break;
      }
      case "assistant": {
        // A reply kept in parts (#2775: consecutive assistant events, each later one marked `continues`) is one answer: a part
        // joins the part before it, in order, so the person reads it whole and Copy copies all of it (W-eebc7cb7, W-fa2f5b77).
        const before = rows[rows.length - 1];
        if (event.body.continues === true && before?.kind === "assistant" && !before.streaming) {
          rows[rows.length - 1] = { ...before, text: before.text + text(event.body.text) };
          break;
        }
        if (text(event.body.text).trim())
          rows.push({ kind: "assistant", key, text: text(event.body.text), streaming: false });
        break;
      }
      case "tool-call": {
        // The call's own result (Codex review of #2525, round 2): a later call that reuses its id says nothing about it.
        const result = ownResults.get(event.seq);
        const outcome = !result
          ? "running"
          : result.ended === true
            ? "not-run"
            : result.isError === true
              ? "error"
              : "done";
        const canvas =
          outcome === "done"
            ? canvasOfCall(
                text(event.body.name),
                event.body.argumentsJson,
                ownResults.get(event.seq),
              )
            : null;
        rows.push({
          kind: "step",
          key,
          label: cloudStepLabel(text(event.body.name), event.body.argumentsJson),
          outcome,
          ...(canvas ? { canvas } : {}),
        });
        break;
      }
      case "gate-wait":
        if (
          (!event.turnKey || !endedTurns.has(event.turnKey)) &&
          !answered.has(`${event.turnKey ?? ""}|${text(event.body.toolCallId)}`)
        ) {
          const argumentsJson = calls.get(text(event.body.toolCallId));
          rows.push({
            kind: "waiting",
            key,
            label: cloudStepLabel(text(event.body.name), argumentsJson ?? "{}"),
            wait: {
              seq: event.seq,
              toolCallId: text(event.body.toolCallId),
              name: text(event.body.name),
              digest: text(event.body.digest),
              text: text(event.body.text),
              turnKey: event.turnKey ?? null,
              step: event.step ?? null,
              argumentsJson: typeof argumentsJson === "string" ? argumentsJson : null,
            },
          });
        }
        break;
      case "turn-end": {
        const outcome = text(event.body.outcome);
        if (event.body.unsent === true && event.turnKey) {
          rows.push({
            kind: "unsent",
            key,
            turnKey: event.turnKey,
            message: text(event.body.message),
            text: unsentWords.get(event.turnKey) ?? "",
          });
          break;
        }
        if (outcome === "stopped" || outcome === "failed")
          rows.push({
            kind: "ended",
            key,
            outcome,
            message: text(event.body.message) || null,
            partial: text(event.body.partial) || null,
          });
        break;
      }
      case "handoff":
        rows.push({ kind: "handoff", key });
        break;
      case "engine-switch": {
        const carried = carriedOf(event.body.carried);
        const to = event.body.engineTo as CloudEngine | undefined;
        if (carried && to && typeof to === "object")
          rows.push({
            kind: "switch",
            key,
            seq: event.seq,
            from: (event.body.engineFrom as CloudEngine | undefined) ?? undefined,
            to,
            carried,
          });
        break;
      }
      case "helper-notice": {
        const notice = helperNoticeOf(event.body);
        if (notice) rows.push({ kind: "notice", key, ...notice });
        break;
      }
      case "canvas-open-observation": {
        const observation = event.body.canvasOpenObservation;
        if (observation && typeof observation === "object" && "state" in observation && observation.state === "refused" && "reason" in observation && typeof observation.reason === "string") rows.push({ kind: "canvas-notice", key, text: observation.reason });
        break;
      }
      case "canvas-open-notice":
        if (text(event.body.text)) rows.push({ kind: "canvas-notice", key, text: text(event.body.text) });
        break;
      case "mac-notice":
        // Only the World's own words for the one line it writes; anything else is no row.
        if (event.body.code === "mac-back" && text(event.body.text) && text(event.body.occurrence))
          rows.push({
            kind: "mac-back",
            key,
            text: text(event.body.text),
            missId: text(event.body.occurrence),
          });
        break;
      default:
        break;
    }
  }
  // Can 21 (Codex review r3 of #3018, P2 5): the words she is writing show only once the history holds everything up to them, so an
  // event written with them (a switch line before a new engine's first words) is never drawn after them.
  const newestSeq = events.at(-1)?.seq ?? 0;
  if (live?.turn && live.turn.text.trim() && live.state !== "idle" && live.lastSeq <= newestSeq)
    rows.push({
      kind: "assistant",
      key: `live-${live.turn.turnKey}`,
      text: live.turn.text,
      streaming: true,
    });
  return rows;
}

/** The line under a folder conversation's title: whose Mac's chosen folders Cleo reads there (step 6.2). Pure; null for any other. */
export const usesFilesOnLine = (
  conversation: Pick<CloudConversationSummary, "macTools">,
): string | null =>
  conversation.macTools
    ? `Uses files on ${conversation.macTools.name?.trim() || "your Mac"}`
    : null;

const conversationOfApprovalRef = makeFunctionReference<
  "query",
  { worldKey: string; gateInstanceKey: string },
  { conversationId: string } | null
>("pocketCloudDoors:conversationOfApproval");

/**
 * Step 7.4.4a-2b: the person's own World conversation an Inbox approval belongs to (null for any other approval), so the
 * Inbox shows and decides that conversation's own card (waitingApprovals, decideGate).
 */
export function useConversationOfApproval(
  worldKey: string | null,
  gateInstanceKey: string | null,
): Loaded<{ conversationId: string } | null> {
  return useWorldQuery<
    { worldKey: string; gateInstanceKey: string },
    { conversationId: string } | null
  >(
    conversationOfApprovalRef as never,
    worldKey === null || gateInstanceKey === null ? null : { worldKey, gateInstanceKey },
  );
}

// W-68125963 (rule 6): Cleo's request that the person pick files (backend fileRequests.js). The Desk and cleo-web draw the same
// card as Pocket (Choose / Not now); Choose opens this computer's own file picker for what she asked for, and the files go up
// through the same attachment door as Pocket's (cleoAttachments stage, PUT, register), then as the person's own message that
// answers her request. Not now answers at once with nothing.
export interface CloudFileRequest {
  readonly requestId: string;
  readonly kinds: ReadonlyArray<string>;
  readonly max: number;
  readonly purpose: string;
  readonly expiresAt: number;
}
const fileRequestOpenForRef = makeFunctionReference<
  "query",
  { conversationId: string },
  CloudFileRequest | null
>("fileRequests:openFor");
const stageFileRef = makeFunctionReference<
  "action",
  {
    conversationId: string;
    mediaType: string;
    byteLength: number;
    name: string;
    requestKey?: string;
  },
  { stageId: string; uploadUrl: string }
>("cleoAttachments:stage");
const registerFileRef = makeFunctionReference<
  "action",
  { conversationId: string; stageId: string; name: string },
  { objectId: string }
>("cleoAttachments:register");
const answerSendRef = makeFunctionReference<
  "mutation",
  {
    conversationId: string;
    text: string;
    clientKey: string;
    timeZone?: string;
    screen?: "desk" | "pocket";
    attachments?: string[];
    fileRequest?: { requestId: string; outcome: "picked" | "declined" };
  },
  SendAnswer
>("cleoTurnSend:send");

/** The types this computer's picker offers for what Cleo asked for (the World's attachable types). */
export const FILE_REQUEST_ACCEPT: Readonly<Record<string, string>> = {
  photos: "image/png,image/jpeg,image/gif,image/webp",
  videos: "video/mp4,video/quicktime",
  pdfs: "application/pdf",
  text: "text/plain,text/csv",
};
export const fileRequestAccept = (kinds: ReadonlyArray<string>): string =>
  kinds.flatMap((kind) => (FILE_REQUEST_ACCEPT[kind] ? [FILE_REQUEST_ACCEPT[kind]] : [])).join(",");

/** The card's first line, in plain words: what Cleo asks the person to pick from this computer. */
export function fileRequestHeadline(request: Pick<CloudFileRequest, "kinds" | "max">): string {
  const words: Record<string, [string, string]> = {
    photos: ["a photo", "photos"],
    videos: ["a video", "videos"],
    pdfs: ["a PDF", "PDFs"],
    text: ["a text file", "text files"],
  };
  const what = request.kinds.map(
    (kind) => (words[kind] ?? [kind, kind])[request.max === 1 ? 0 : 1],
  );
  const joined =
    what.length <= 1 ? (what[0] ?? "files") : `${what.slice(0, -1).join(", ")} or ${what.at(-1)}`;
  return `Cleo would like ${joined} from your computer`;
}

export async function readFileRequest(
  client: NonNullable<ReturnType<typeof useWorldConvexClient>>,
  conversationId: string,
): Promise<CloudFileRequest | null> {
  return client.query(fileRequestOpenForRef, { conversationId });
}

// The World's limits for one file and one message (backend lib/conversationAttachments.js), checked before any byte goes up, as
// Pocket checks them (cloudConversationFiles.ts); the World checks them again (Codex review of #2973).
const MiB = 1024 * 1024;
const FILE_CEILINGS: Readonly<Record<string, number>> = {
  "image/png": 5 * MiB,
  "image/jpeg": 5 * MiB,
  "image/gif": 5 * MiB,
  "image/webp": 5 * MiB,
  "application/pdf": 6 * MiB,
  "text/plain": 1 * MiB,
  "text/csv": 1 * MiB,
  "video/mp4": 32 * MiB,
  "video/quicktime": 32 * MiB,
};
/** Why these files cannot answer Cleo's request, in plain words, or null when they can. Pure. */
export function fileRequestFilesProblem(
  files: ReadonlyArray<{ name: string; type: string; size: number }>,
): string | null {
  let read = 0;
  let video = 0;
  for (const file of files) {
    const type = file.type.split(";")[0]!.trim().toLowerCase();
    const ceiling = FILE_CEILINGS[type];
    if (ceiling === undefined)
      return `'${file.name}' cannot be given to Cleo: photos (PNG, JPEG, GIF, WebP), videos (MP4, QuickTime), PDFs, text files and CSV files can.`;
    if (file.size <= 0) return `'${file.name}' is empty.`;
    if (file.size > ceiling) return `'${file.name}' is over ${Math.round(ceiling / MiB)} MB.`;
    if (type.startsWith("video/")) video += file.size;
    else read += file.size;
  }
  if (read > 6 * MiB) return "These files are over 6 MB together; choose fewer.";
  if (video > 64 * MiB) return "These videos are over 64 MB together; choose fewer.";
  return null;
}

/** Uploads the files the person chose (each through the attachment door) and sends them as the answer to Cleo's request. */
export async function answerFileRequest(
  client: NonNullable<ReturnType<typeof useWorldConvexClient>>,
  conversationId: string,
  request: CloudFileRequest,
  files: ReadonlyArray<File>,
  fetchImpl: typeof fetch = fetch,
): Promise<SendAnswer> {
  if (files.length < 1 || files.length > request.max) {
    throw new Error(
      `Choose 1 to ${request.max} file${request.max === 1 ? "" : "s"} for Cleo's request.`,
    );
  }
  const problem = fileRequestFilesProblem(files);
  if (problem) throw new Error(problem);
  const ids: string[] = [];
  for (const file of files) {
    const staged = await client.action(stageFileRef, {
      conversationId,
      mediaType: file.type,
      byteLength: file.size,
      name: file.name,
      // @effect-diagnostics-next-line cryptoRandomUUID:off
      requestKey: crypto.randomUUID(),
    });
    let put: Response;
    try {
      put = await fetchImpl(staged.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
    } catch {
      throw new Error(`'${file.name}' was not uploaded. Check your connection and try again.`);
    }
    if (!put.ok)
      throw new Error(`'${file.name}' was not uploaded. Check your connection and try again.`);
    ids.push(
      (
        await client.action(registerFileRef, {
          conversationId,
          stageId: staged.stageId,
          name: file.name,
        })
      ).objectId,
    );
  }
  const timeZone = reportedTimeZone();
  return client.mutation(answerSendRef, {
    conversationId,
    text: "",
    // @effect-diagnostics-next-line cryptoRandomUUID:off
    clientKey: crypto.randomUUID(),
    ...(timeZone ? { timeZone } : {}),
    screen: "desk",
    attachments: ids,
    fileRequest: { requestId: request.requestId, outcome: "picked" },
  });
}

/** Not now: the person's own short answer to Cleo's request, with nothing picked. */
export async function declineFileRequest(
  client: NonNullable<ReturnType<typeof useWorldConvexClient>>,
  conversationId: string,
  request: CloudFileRequest,
): Promise<SendAnswer> {
  const timeZone = reportedTimeZone();
  return client.mutation(answerSendRef, {
    conversationId,
    text: "Not now",
    // @effect-diagnostics-next-line cryptoRandomUUID:off
    clientKey: crypto.randomUUID(),
    ...(timeZone ? { timeZone } : {}),
    screen: "desk",
    fileRequest: { requestId: request.requestId, outcome: "declined" },
  });
}
